"use client";

import { Loader } from "@/components/ui/loader";
import {
  type SpondProfileSummary,
  useSpondProfile,
} from "@/hooks/useSpondProfile";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import ContinueInAppState from "./ContinueInAppState";

interface EmbeddedLoginGateProps {
  /**
   * Called once a valid session is confirmed, to advance past the login step.
   * Receives the profile this gate already fetched to confirm the session, so
   * the details steps that follow can reuse it instead of fetching again.
   */
  onAuthenticated: (profile?: SpondProfileSummary) => void;
}

/**
 * Replaces the funnel's login step inside the embedded WebView, where no
 * sign-in form is ever shown. A valid session (e.g. right after /auth/sso
 * redeemed a code) can only be told apart from a missing/expired one by
 * asking /api/profile — and must not be blocked behind a state that never
 * advances.
 *
 * Only a 401 (missing/expired session) shows ContinueInAppState. A backend
 * fault (502/network) is not a session expiry — there is no sign-in form to
 * fall back to here (unlike the details steps), so those land on a generic
 * error instead of misreporting the session as expired.
 *
 * Does not use useEmbeddedSessionExpiry: that hook also tracks submit-time
 * 401s and gates on sessionTransfer, neither of which applies here (mount-only
 * check, only rendered where the app transfers sessions).
 */
export default function EmbeddedLoginGate({
  onAuthenticated,
}: EmbeddedLoginGateProps) {
  const t = useTranslations("publicEventSignup");
  const { profile, loading, error, status } = useSpondProfile();

  // onAuthenticated is expected to be stable (wrapped in useCallback by the
  // caller) — it is intentionally omitted so a parent re-render alone can't
  // re-fire this past the one real loading/error transition.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional, see above
  useEffect(() => {
    if (!loading && !error) {
      onAuthenticated(profile);
    }
  }, [loading, error, profile]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    if (status === 401) {
      return <ContinueInAppState />;
    }

    return (
      <div className="flex h-full flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold">
            {t("embeddedLoginError.header")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("embeddedLoginError.body")}
          </p>
        </div>
      </div>
    );
  }

  // A valid session was found — the effect above advances the funnel past
  // this step; render nothing while that happens.
  return null;
}
